import { Editor, EditorContent, EditorContext, flattenExtensions, isNodeSelection, useEditor, type Content } from '@tiptap/react';
import * as React from 'react';
import authService from '../../services/zybook-auth-service.ts';

// --- Tiptap Core Extensions ---
import { TiptapCollabProvider } from '@tiptap-pro/provider';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';
import { Details, DetailsContent, DetailsSummary, type DetailsOptions } from '@tiptap/extension-details';
import { FileHandler } from '@tiptap/extension-file-handler';
import { BulletList, OrderedList, TaskItem, type BulletListOptions, type OrderedListOptions } from '@tiptap/extension-list';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyleKit } from '@tiptap/extension-text-style';
import { StarterKit } from '@tiptap/starter-kit';
import * as Y from 'yjs';
import { Iframe } from './tiptap-extension/iframe-extension/iframe-extension';
import { Link } from './tiptap-extension/link-extension';
import { OrderedListTypeInputRule } from './tiptap-extension/ordered-list-type-input-rule-extension';
import { Reference } from './tiptap-extension/reference-extension/reference-extension';
import { getOrCreateReferenceStore } from './tiptap-extension/reference-extension/reference-store';

// --- Tiptap Pro Extensions ---
import { mapSnapshotCompareDiffToDecorations } from '@/lib/snapshot/snapshot-user';
import { makeGuidNotStartingWithDigit } from '@/lib/streamlit-utils';
import { CommentsKit, type ThreadOptions } from '@tiptap-pro/extension-comments';
import Snapshot, { type CollabOnUpdateProps } from '@tiptap-pro/extension-snapshot';
import { SnapshotCompare } from '@tiptap-pro/extension-snapshot-compare';
import UniqueID from '@tiptap/extension-unique-id';
import { v4 as uuidv4 } from 'uuid';

// --- Custom Extensions ---
import '@/components/tiptap-editor/tiptap-node/table-node/styles/prosemirror-table.scss';
import '@/components/tiptap-editor/tiptap-node/table-node/styles/table-node.scss';
import { syncMaImageAltTextInEditor } from '@/lib/ma-image-alt-text';
import 'katex/dist/katex.min.css';
import { TiptapEditorPlugins } from './tiptap-editor-plugins.tsx';
import { BlockOrderingBuilderNode } from './tiptap-extension/block-ordering-builder-extension/block-ordering-builder-extension';
import CodeOutputBuilderNode from './tiptap-extension/code-output-builder-extension/code-output-builder-extension';
import CodeWritingBuilderNode from './tiptap-extension/code-writing-builder-extension/code-writing-builder-extension';
import { CollaborationNodePresence } from './tiptap-extension/collaboration-node-presence/collaboration-node-presence-extension';
import CustomDocument from './tiptap-extension/custom-document-extension/index.ts';
import DetectAnswer from './tiptap-extension/detect-answer-extension';
import { DragScroll } from './tiptap-extension/drag-scroll-extension/drag-scroll-extension.ts';
import { ZyFloatingMenuSpec } from './tiptap-extension/floating-menu-extension/floating-menu-extension.ts';
import { MaImageDocumentSync } from './tiptap-extension/ma-image-document-sync-extension/ma-image-document-sync-extension';
import Matching from './tiptap-extension/matching-extension';
import Mathematics, { migrateZybookMathStrings, type MathematicsOptions } from './tiptap-extension/math-extension';
import './tiptap-extension/math-extension/styles.scss';
import MultipleChoice from './tiptap-extension/multiple-choice-extension';
import OpenStaxPythonCodeRunnerBuilderNodeView from './tiptap-extension/open-stax-python-code-runner-builder-extension/open-stax-python-code-runner-builder-extension';
import ParsonsCodingBuilderNode from './tiptap-extension/parsons-coding-builder-extension/parsons-coding-builder-extension.ts';
import { ProgressionBuilderNode } from './tiptap-extension/progression-builder-extension/progression-builder-extension';
import ProtectedNode, {
    type ProtectedNodeDeletePromptContext,
    type ProtectedNodeDeletionInfo,
    type ProtectedNodeOptions,
    type ReferencedResourceDeletionInfo,
} from './tiptap-extension/protected-node-extension';
import { ScrollHandler } from './tiptap-extension/scroll-handler-extension/scroll-handler-extension';
import { Selection } from './tiptap-extension/selection-extension';
import ShortAnswer from './tiptap-extension/short-answer-extension';
import { StreamlitAppNode, type StreamlitAppNodeOptions } from './tiptap-extension/streamlit-app-extension';
import { taskList } from './tiptap-extension/taskList-extension';
import TestQuestion from './tiptap-extension/test-question-extension';
import UnsupportedEditableNode from './tiptap-extension/unsupported-editable-extension/unsupported-editable-extension.ts';
import UnsupportedNode from './tiptap-extension/unsupported-node-extension';
import { ZyTableOfContents, type ZyTableOfContentsOptions } from './tiptap-extension/zy-table-of-contents-extension';
import { ZyAnimatorNode } from './tiptap-extension/zyanimator-extension/zyanimator-extension.ts';
import ZyAside from './tiptap-extension/zyaside-extension/zyaside-extension';
import ZyBlock from './tiptap-extension/zyblock-extension/zyblock-extension';
import { ZyBold } from './tiptap-extension/zybold-extension/zybold-extension.ts';
import ZyClassicLab from './tiptap-extension/zyclassiclab-extension/index.ts';
import ZyCode from './tiptap-extension/zycode-extension';
import ZyCodeInline from './tiptap-extension/zycode-inline-extension/zycode-inline-extension.ts';
import { ZyCommand } from './tiptap-extension/zycommand-extension.ts';
import ZyCommonError from './tiptap-extension/zycommonerror-extension/zycommonerror-extension';
import ZyConsole from './tiptap-extension/zyconsole-extension/zyconsole-extension';
import ZyDE from './tiptap-extension/zyde-extension';
import ZyDefinition from './tiptap-extension/zydefn-extension/zydefn.ts';
import { ZyDocument, type ZyDocumentOptions } from './tiptap-extension/zydocument-extension.ts';
import ZyExampleNode from './tiptap-extension/zyexample-extension/zyexample-extension.ts';
import ZyExercise from './tiptap-extension/zyexercise-extension';
import ZyExplore from './tiptap-extension/zyexplore-extension/zyexplore-extension.ts';
import ZyFootnote from './tiptap-extension/zyfootnote-extension/zyfootnote-extension';
import { ZyGdocDrawing } from './tiptap-extension/zyGdocDrawing-extension/zyGdocDrawing-extension.ts';
import { ZyHighlight } from './tiptap-extension/zyhighlight-extension/zyhighlight-extension.ts';
import { ZyImage } from './tiptap-extension/zyimage-extension/zyimage-extension.ts';
import { ZyInlineImage } from './tiptap-extension/zyinline-image-extension/zyinline-image-extension.ts';
import ZyInlineEnum from './tiptap-extension/zyinlineenum-extension/zyinlineenum-extension.ts';
import { ZyItalic } from './tiptap-extension/zyitalic-extension/zyitalic-extension.ts';
import ZyITLab from './tiptap-extension/zyitlab-extension/index.ts';
import { ZyMathWorksLearningToolNode } from './tiptap-extension/zymathworks-learning-tool-extension/zymathworks-learning-tool-extension';
import ZyMatrix from './tiptap-extension/zymatrix-extension/zymatrix-extension.ts';
import ZyProof from './tiptap-extension/zyproof-extension/zyproof-extension.ts';
import { ZySearchAndReplace } from './tiptap-extension/zysearch-replace-extension/zysearch-replace-extension.ts';
import { ZyStrike } from './tiptap-extension/zystrike-extension/zystrike-extension.ts';
import ZyStudio from './tiptap-extension/zystudio-extension/index.ts';
import ZyTerm from './tiptap-extension/zyterm-extension/zyterm-extension.ts';
import { ZyTermPasteBlock } from './tiptap-extension/zyterm-extension/zyterm-paste-block-extension';
import {
    ZyText,
    findNextFocusableElement,
    findNextParagraphAfterElement,
    findPreviousFocusableElement,
    findPreviousParagraphBeforeElement,
} from './tiptap-extension/zytext-extension.ts';
import { ZyToolNode } from './tiptap-extension/zytool-extension/zytool-extension';
import ZyVersion, { type ZyVersionOptions } from './tiptap-extension/zyversion-extension';
import {
    ZySplitViewCaret,
    type ZySplitViewCaretOptions,
} from './tiptap-extension/zyversion-extension/zyversion-splitviewcaret-extension.ts';
import ZyYoutubeVideo from './tiptap-extension/zyyoutubevideo-extension';

// --- UI Primitives ---
import { Button } from './tiptap-ui-primitive/button';
import { Separator } from './tiptap-ui-primitive/separator';
import { Spacer } from './tiptap-ui-primitive/spacer';
import { Toolbar, ToolbarGroup, ToolbarSeparator } from './tiptap-ui-primitive/toolbar';

// --- Global styles ---
import '../../styles/tiptap/_keyframe-animations.scss';
import '../../styles/tiptap/_variables.scss';

// --- Tiptap Node ---
import './tiptap-node/code-block-node/code-block-node.scss';
import './tiptap-node/image-node/image-node.scss';
import { ImageUploadNode, type ImageUploadNodeOptions } from './tiptap-node/image-upload-node/image-upload-node-extension';
import './tiptap-node/list-node/list-node.scss';
import './tiptap-node/paragraph-node/paragraph-node.scss';

// --- Tiptap UI ---
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.tsx';
import QuestionSet from './tiptap-extension/question-set-extension/question-set-extension.ts';
import ZyCitation from './tiptap-extension/zycitation-extension/zycitation-extension.ts';
import { ZyPlaceholder } from './tiptap-extension/zyplaceholder-extension/zyplaceholder-extension.ts';
import { ZyTableKit } from './tiptap-extension/zytable-extension/zytable-extension.ts';
import BlockquoteMenu from './tiptap-ui/blockquote-menu/blockquote-menu.tsx';
import { ColorHighlightPopover, ColorHighlightPopoverButton, ColorHighlightPopoverContent } from './tiptap-ui/color-highlight-popover';
import CommentButton from './tiptap-ui/comment-button/comment-button';
import CopySectionReferenceButton from './tiptap-ui/copy-section-reference-button/copy-section-reference-button';
import DetailsButton from './tiptap-ui/details-button/details-button';
import { GridLayoutTriggerButton } from './tiptap-ui/grid-layout-trigger-button';
import { HeadingDropdownMenu } from './tiptap-ui/heading-dropdown-menu';
import { IframeButton } from './tiptap-ui/iframe-button';
import ImageDropdownMenu from './tiptap-ui/image-dropdown-menu/image-dropdown-menu';
import { LinkButton, LinkContent, LinkPopover } from './tiptap-ui/link-popover';
import { ListDropdownMenu } from './tiptap-ui/list-dropdown-menu';
import { MarkButton } from './tiptap-ui/mark-button';
import MathButton from './tiptap-ui/math-button/math-button';
import { OverflowMenu } from './tiptap-ui/overflow-menu/overflow-menu';
import { getToolbarTier } from './tiptap-ui/overflow-menu/toolbar-overflow-config';
import QuestionSetMenu from './tiptap-ui/question-set-menu/question-set-menu.tsx';
import { ScriptDropdownMenu } from './tiptap-ui/script-dropdown-menu';
import SnapshotCompareUserLegend from './tiptap-ui/snapshot-compare-user-legend/snapshot-compare-user-legend';
import TableDropdownMenu from './tiptap-ui/table-dropdown-menu/table-dropdown-menu';
import { TextAlignDropdownMenu } from './tiptap-ui/text-align-dropdown-menu';
import { UndoRedoButton } from './tiptap-ui/undo-redo-button';
import ZyAsideButton from './tiptap-ui/zyaside-button/zyaside-button';
import ZyCodeButton from './tiptap-ui/zycode-button/zycode-button.tsx';
import ZyConsoleButton from './tiptap-ui/zyconsole-button/zyconsole-button.tsx';
import ZyDEButton from './tiptap-ui/zyde-button/zyde-button.tsx';
import ZyDefnButton from './tiptap-ui/zydefn-button/zydefn-button';
import ZyExampleMenu from './tiptap-ui/zyexample-menu/zyexample-menu.tsx';
import ZyExerciseButton from './tiptap-ui/zyexercise-button/zyexercise-button';
import ZyExploreButton from './tiptap-ui/zyexplore-button/zyexplore-button';
import ZyFootnoteButton from './tiptap-ui/zyfootnote-button/zyfootnote-button';
import ZyInlineEnumButton from './tiptap-ui/zyinlineenum-button/zyinlineenum-button';
import ZyProofButton from './tiptap-ui/zyproof-button/zyproof-button';
import ZyStudioButton from './tiptap-ui/zystudio-button/zystudio-button';
import ZyTermButton from './tiptap-ui/zyterm-button/zyterm-button';
import ZyTestQuestionButton from './tiptap-ui/zytestquestion-button/zytestquestion-button';
import ZyToolDropdownMenu from './tiptap-ui/zytools-dropdown-menu/zytools-dropdown-menu';
import ZyVersionDropdown from './tiptap-ui/zyversion-dropdown/zyversion-dropdown';
import ZyVersionScrollLockButton from './tiptap-ui/zyversion-scrolllock-button/zyversion-scrolllock-button';
import ZyVersionSelectionLegend from './tiptap-ui/zyversion-selection-legend/zyversion-selection-legend';
import ZyVersionSplitViewButton from './tiptap-ui/zyversion-splitview-button/zyversion-splitview-button';
import ZyVersionWrapButton from './tiptap-ui/zyversion-wrap-button/zyversion-wrap-button';
import ZyYoutubeVideoButton from './tiptap-ui/zyyoutubevideo-button/zyyoutubevideo-button';

// --- zyComponents ---
import { SearchReplacePopover } from '../common/search-replace-popover';
import { SplitViewPanes } from './split-view-panes';

// --- Icons ---
import { CodeIcon, LoaderCircle } from 'lucide-react';
import { ArrowLeftIcon } from './tiptap-icons/arrow-left-icon';
import { HighlighterIcon } from './tiptap-icons/highlighter-icon';
import { LinkIcon } from './tiptap-icons/link-icon';

// --- Hooks ---
import { useCursorVisibility } from '../../hooks/use-cursor-visibility';
import { useIsEditorReadonly } from '../../hooks/use-is-editor-readonly';
import { useMobile } from '../../hooks/use-mobile';
import { useSplitView } from '../../hooks/use-split-view.ts';
import { useTiptapCollaborationProvider } from '../../hooks/use-tiptap-collaboration-provider.ts';
import { useToolbarHeight } from '../../hooks/use-toolbar-height';
import { useToolbarWidth } from '../../hooks/use-toolbar-width';
import { useWindowSize } from '../../hooks/use-window-size';

// --- Components ---
import { GlobalTopBar } from '@/components/layout/global-top-bar';
import { Button as ZybooksButton } from '../ui/button';
import { CollaborationPresence } from './collaboration-presence';

// --- Lib ---
import { scrollEditorToContentResourceGuid } from '@/lib/get-content-resource-guid-at-viewport-top';
import { withScrollSyncApply } from '@/lib/split-view-scroll-sync';
import { ACCEPTED_IMAGE_TYPES, MAX_FILE_SIZE } from '@/lib/utils.ts';
import { handleImageUpload, uploadAndInsertImageFromFile } from '../../lib/zy-tiptap-utils';

// --- Styles ---
import './styles.scss';

import type { EditorProps } from '@tiptap/pm/view';
import { toast } from 'sonner';
import { FullscreenBoundaryContext } from './tiptap-ui/fullscreen-boundary-context.ts';
import { ZyVersionFilterContext } from './tiptap-ui/zyversion-filter-context.ts';

type TiptapEditorInnerProps = TiptapEditorProps & {
    ydoc: Y.Doc | null;
    collabProvider: TiptapCollabProvider | null;
};

interface TiptapEditorProps {
    content?: Content;
    isInitialContentLoaded?: boolean;
    tiptapDocumentName?: string;
    useCollaboration: boolean;
    editable?: boolean;
    editorProps?: Partial<EditorProps>;
    extensionOptions?: ExtensionOptions;
    omitExtensions?: string[];
    /** When false, hides version filter / split-view / wrap toolbar controls. Defaults to true. */
    showZyVersionToolbar?: boolean;
    enableCodeEditor?: boolean;
    onCreate?: (instances: TiptapOnCreateCallbackProps) => void;
    onUpdate?: (editor: Editor) => void;
    onTransaction?: (props: Parameters<NonNullable<Editor['options']['onTransaction']>>[0]) => void;
    onDelete?: (props: Parameters<NonNullable<Editor['options']['onDelete']>>[0]) => void;
    onToggleCodeEditor?: () => void;
    /** Rendered left of the main editor inside the scroll container, so it scrolls with the document. Hidden in split view. */
    leftGutter?: React.ReactNode;
}

export interface TiptapOnCreateCallbackProps {
    editor: Editor;
    collabProvider?: TiptapCollabProvider;
}

export type TiptapEditorHandle = {
    getEditorForPreviewVersion(previewVersion?: string): Editor | null;
    scrollToContentResourceGuidForPreviewExit(guid: string, previewVersion?: string): void;
};

export interface CollaborationCaretOptions {
    /** The renderer to use for math nodes, either 'mathjax' or 'katex'. Default to 'mathjax' if not specified */
    user?: {
        name: string;
        color: string;
    };
}

export interface ExtensionOptions {
    zyDocument?: Partial<ZyDocumentOptions>;
    bulletList?: Partial<BulletListOptions>;
    orderedList?: Partial<OrderedListOptions>;
    zyVersion?: Partial<ZyVersionOptions>;
    protectedNode?: Partial<ProtectedNodeOptions>;
    mathematics?: Partial<MathematicsOptions>;
    collaborationCaret?: Partial<CollaborationCaretOptions>;
    zySplitViewCaret?: Partial<ZySplitViewCaretOptions>;
    imageUpload?: Partial<ImageUploadNodeOptions>;
    details?: Partial<DetailsOptions>;
    comments?: Partial<ThreadOptions>;
    tableOfContents?: Partial<ZyTableOfContentsOptions>;
    snapshot?: {
        onUpdate?: (payload: CollabOnUpdateProps) => void;
    };
    snapshotCompare?: {
        enabled?: boolean;
    };
    streamlitApp?: StreamlitAppNodeOptions;
}

export function getExtensions(
    ydoc?: Y.Doc | null,
    options: ExtensionOptions = {},
    omitExtensions: string[] = [],
    collabProvider?: TiptapCollabProvider | null,
) {
    const extensions = [];
    const listExtendedConfig = { content: '(listItem | zyVersionBlock)+' };

    // If collaborating.
    if (ydoc) {
        extensions.push(
            // Disables default Undo/Redo extension to use Collaboration's history management.
            StarterKit.configure({
                undoRedo: false,
                document: false,
                link: false,
                code: false,
                text: false,
                dropcursor: {
                    width: 4,
                    class: 'text-blue-200',
                },
                trailingNode: omitExtensions.includes('trailingNode') ? false : undefined,
                bulletList: false,
                orderedList: false,
                italic: false,
                strike: false,
                bold: false,
            }),
            Collaboration.configure({
                document: ydoc,
            }),
        );

        if (collabProvider && !omitExtensions.includes('collaborationCaret')) {
            const { user: caretUserOption } = options.collaborationCaret ?? {};
            const caretUser = caretUserOption ?? authService.getCollaborationUser();
            extensions.push(
                CollaborationCaret.configure({
                    provider: collabProvider,
                    user: caretUser,
                }),
            );
        }

        if (collabProvider && !omitExtensions.includes('collaborationNodePresence')) {
            extensions.push(
                CollaborationNodePresence.configure({
                    provider: collabProvider,
                }),
            );
        }

        if (collabProvider && !omitExtensions.includes('comments')) {
            extensions.push(
                CommentsKit.configure({
                    provider: collabProvider,
                    trackedChangesIntegration: false,
                    useLegacyWrapping: false,
                    ...options.comments,
                }),
            );
        }

        if (collabProvider && !omitExtensions.includes('collaborationHistory')) {
            extensions.push(
                Snapshot.configure({
                    provider: collabProvider,
                    onUpdate: options.snapshot?.onUpdate,
                }),
                SnapshotCompare.configure({
                    provider: collabProvider,
                    mapDiffToDecorations: mapSnapshotCompareDiffToDecorations,
                }),
            );
        } else if (options.snapshotCompare?.enabled && !omitExtensions.includes('snapshotCompare')) {
            extensions.push(
                SnapshotCompare.configure({
                    provider: null,
                    mapDiffToDecorations: mapSnapshotCompareDiffToDecorations,
                }),
            );
        }
    } else {
        extensions.push(
            StarterKit.configure({
                document: false,
                link: false,
                code: false,
                text: false,
                trailingNode: omitExtensions.includes('trailingNode') ? false : undefined,
                dropcursor: {
                    width: 4,
                    class: 'text-blue-200',
                },
                bulletList: false,
                orderedList: false,
                italic: false,
                strike: false,
                bold: false,
            }),
        );

        if (options.snapshotCompare?.enabled && !omitExtensions.includes('snapshotCompare')) {
            extensions.push(
                SnapshotCompare.configure({
                    provider: null,
                    mapDiffToDecorations: mapSnapshotCompareDiffToDecorations,
                }),
            );
        }
    }

    extensions.push(
        // Standard Extensions everyone has access to
        ZyText,
        ZyDocument.configure(options.zyDocument ?? {}),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        taskList,
        TaskItem.configure({ nested: true }),
        ZyHighlight.configure({ multicolor: true }),
        Details.configure(options.details || {}),
        DetailsSummary,
        DetailsContent,
        Link.configure({
            openOnClick: false,
            enableClickSelection: false,
            HTMLAttributes: {
                class: 'pointer-events-none',
            },
        }),
        Superscript,
        Subscript,
        TextStyleKit,
        Selection,
        Mathematics.configure(options.mathematics || {}),
        ImageUploadNode.configure(options.imageUpload || {}),
        FileHandler.configure({
            onPaste: (editor, files, htmlContent) => {
                if (htmlContent && htmlContent.length > 0) {
                    return;
                }

                const file = files[0];
                if (file) {
                    uploadAndInsertImageFromFile(editor, file);
                }
            },
            onDrop: (editor, files, pos) => {
                const file = files[0];
                if (file) {
                    uploadAndInsertImageFromFile(editor, file, pos);
                }
            },
        }),

        // zyBooks Custom Extensions that everyone has access to
        UnsupportedEditableNode,
        ZyFloatingMenuSpec,
        UnsupportedNode,
        DragScroll,
        ScrollHandler,
        ZyPlaceholder,
        QuestionSet.configure({
            multipleChoice: true,
            shortAnswer: true,
            matching: true,
            detectAnswer: true,
        }),
        ZyDefinition,
        ZyTerm,
        ZyTermPasteBlock,
        MultipleChoice,
        ZyAside,
        ZyImage,
        ZyInlineImage,
        MaImageDocumentSync,
        ZyMatrix,
        ZyTableKit.configure({
            table: {
                resizable: true,
            },
        }),
        ZyBlock,
        ZyCode,
        ZyCodeInline,
        ZyCommand,
        ZyConsole,
        ZyExercise,
        ZyItalic,
        ZyInlineEnum,
        ZyProof,
        ZyStrike,
        ZyBold,
        ZySearchAndReplace,
        // Allow-list: available without seeAllMAContent
        ZyFootnote,
        CustomDocument,
        ShortAnswer,
        Matching,
        DetectAnswer,
        ZyAnimatorNode,
        ZyExplore,
        ZyExampleNode,
        ZyVersion.configure(options.zyVersion || {}),
        // zyBooks Custom extensions that only those with the `canSeeAllMAContent` flag can see
        ...(authService.canSeeAllMAContent()
            ? [
                  TestQuestion,
                  Iframe,
                  ZyCitation,
                  ZyCommonError,
                  ZyDE,
                  ZyToolNode,
                  ZyMathWorksLearningToolNode,
                  ZyYoutubeVideo,
                  ZyStudio,
                  ZyClassicLab,
                  ZyITLab,
                  Reference,
                  ProgressionBuilderNode,
                  BlockOrderingBuilderNode,
                  CodeWritingBuilderNode,
                  CodeOutputBuilderNode,
                  ParsonsCodingBuilderNode,
                  OpenStaxPythonCodeRunnerBuilderNodeView,
                  StreamlitAppNode.configure(options.streamlitApp ?? {}),
                  ZyGdocDrawing,
              ]
            : []),

        BulletList.extend(listExtendedConfig).configure(options.bulletList || {}),
        OrderedList.extend(listExtendedConfig).configure(options.orderedList || {}),
        OrderedListTypeInputRule,
    );

    if (options.zySplitViewCaret?.enabled) {
        extensions.push(ZySplitViewCaret.configure({ enabled: true }));
    }

    const nodesWithoutIds = ['text', 'doc'];
    extensions.push(
        UniqueID.configure({
            types: flattenExtensions(extensions)
                .filter(extension => extension.type === 'node' && !nodesWithoutIds.includes(extension.name))
                .map((extension: any) => extension.name),
            // Streamlit guids become K8s DNS-1035 Service names and cannot start with a digit.
            // https://tiptap.dev/docs/editor/extensions/functionality/uniqueid#generateid
            generateID: ({ node }) => (node.type.name === StreamlitAppNode.name ? makeGuidNotStartingWithDigit() : uuidv4()),
        }),
        ProtectedNode.configure(options.protectedNode || {}),
    );

    if (options.tableOfContents) {
        extensions.push(
            ZyTableOfContents.configure({
                anchorTypes: ['heading'],
                ...options.tableOfContents,
            }),
        );
    }

    return extensions.filter(extension => !omitExtensions.includes(extension.name));
}

export function getEditorProps(overrideProps?: Partial<EditorProps>): EditorProps {
    const defaultEditorProps: EditorProps = {
        attributes: {
            autocomplete: 'off',
            autocorrect: 'off',
            autocapitalize: 'off',
            'aria-label': 'Main content area, start typing to enter text.',
        },
        scrollThreshold: {
            top: 0,
            bottom: 100,
            left: 0,
            right: 0,
        },
        scrollMargin: {
            top: 0,
            bottom: 100,
            left: 0,
            right: 0,
        },
    };

    if (!overrideProps) {
        return defaultEditorProps;
    }

    return {
        ...defaultEditorProps,
        ...overrideProps,
    };
}

const MainToolbarContent = ({
    onHighlighterClick,
    onLinkClick,
    onToggleCodeEditor,
    isMobile,
    enableCodeEditor = false,
    isSplitView = false,
    isSyncScroll = true,
    onToggleSplitView,
    onToggleSyncScroll,
    onSyncScroll,
    width,
    mainEditor,
    showZyVersionToolbar,
}: {
    onToggleCodeEditor?: () => void;
    onHighlighterClick: () => void;
    onLinkClick: () => void;
    isMobile: boolean;
    enableCodeEditor?: boolean;
    isSplitView?: boolean;
    isSyncScroll?: boolean;
    onToggleSplitView?: () => void;
    onToggleSyncScroll?: () => void;
    onSyncScroll?: () => void;
    width: number;
    mainEditor: Editor | null;
    showZyVersionToolbar: boolean;
}) => {
    const tier = getToolbarTier(width);
    const tier1Buttons = (
        <>
            <MarkButton
                key="mark-bold"
                type="bold"
            />
            <MarkButton
                key="mark-italic"
                type="italic"
            />
            <MarkButton
                key="mark-strike"
                type="strike"
            />
            <MarkButton
                key="mark-code"
                type="code"
            />
            <MarkButton
                key="mark-underline"
                type="underline"
            />
            {!isMobile ? (
                <ColorHighlightPopover key="color-highlight" />
            ) : (
                <ColorHighlightPopoverButton
                    key="color-highlight"
                    onClick={onHighlighterClick}
                />
            )}
            {!isMobile ? (
                <LinkPopover
                    autoOpenOnLinkActive={false}
                    key="link"
                />
            ) : (
                <LinkButton
                    key="link"
                    onClick={onLinkClick}
                />
            )}
            <CopySectionReferenceButton key="copy-section-reference" />
            <CommentButton key="comment" />
        </>
    );

    const tier2Buttons = <ScriptDropdownMenu key="script-dropdown" />;

    const tier3Buttons = (
        <>
            <TextAlignDropdownMenu key="align-dropdown" />
            <GridLayoutTriggerButton key="grid-layout" />
        </>
    );

    const tier4Buttons = (
        <>
            <DetailsButton key="details" />
            <ZyDefnButton key="zydefn" />
            <ZyTermButton key="zyterm" />
            <ZyFootnoteButton key="zyfootnote" />
            <ZyInlineEnumButton key="zyinlineenum" />
            <ZyConsoleButton key="zyconsole" />
            <ZyDEButton key="zyde" />
            <ZyCodeButton key="zycode" />
        </>
    );

    const tier5Buttons = (
        <>
            <MathButton
                key="math-inline"
                mathType="inline"
            />
            <MathButton
                key="math-block"
                mathType="block"
            />
            <ZyExampleMenu key="zyexample" />
            <TableDropdownMenu key="table" />
            <QuestionSetMenu key="questionset" />
            <ZyTestQuestionButton key="zytestquestion" />
            <ZyProofButton key="zyproof" />
            <ZyAsideButton key="zyaside" />
        </>
    );

    const tier6Buttons = (
        <>
            <ZyExploreButton key="zyexplore" />
            <ZyExerciseButton key="zyexercise" />
            <ZyYoutubeVideoButton key="zyyoutube" />
            <ZyStudioButton key="zystudio" />
            <ZyToolDropdownMenu key="zytool" />
            <Separator orientation="vertical" />
            <ImageDropdownMenu key="image" />
            <IframeButton key="iframe" />
        </>
    );

    const splitViewButtons = (
        <>
            <ZyVersionDropdown editor={mainEditor} />
            {onToggleSplitView && (
                <ZyVersionSplitViewButton
                    isSplitView={isSplitView}
                    onToggleSplitView={onToggleSplitView}
                />
            )}
            {isSplitView && onToggleSyncScroll && onSyncScroll && (
                <ZyVersionScrollLockButton
                    isSyncScroll={isSyncScroll}
                    onToggleSyncScroll={onToggleSyncScroll}
                    onSyncScroll={onSyncScroll}
                />
            )}
            <ZyVersionWrapButton />
        </>
    );

    const overflowGroups: React.ReactNode[] = [];
    if (tier < 1) {
        overflowGroups.push(tier1Buttons);
        overflowGroups.push(<Separator orientation="vertical" />);
    }
    if (tier < 2) {
        overflowGroups.push(tier2Buttons);
        overflowGroups.push(<Separator orientation="vertical" />);
    }
    if (tier < 3) {
        overflowGroups.push(tier3Buttons);
        overflowGroups.push(<Separator orientation="vertical" />);
    }
    if (tier < 4) {
        if (showZyVersionToolbar) {
            overflowGroups.push(splitViewButtons);
        }
        overflowGroups.push(tier4Buttons);
    }
    if (tier < 5) overflowGroups.push(tier5Buttons);
    if (tier < 6) overflowGroups.push(tier6Buttons);

    return (
        <>
            {tier === 6 && <Spacer />}

            <ToolbarGroup>
                <UndoRedoButton action="undo" />
                <UndoRedoButton action="redo" />
            </ToolbarGroup>

            <ToolbarSeparator />

            <ToolbarGroup>
                <HeadingDropdownMenu levels={[1, 2, 3, 4]} />
                <ListDropdownMenu types={['bulletList', 'orderedList']} />
                <BlockquoteMenu />
            </ToolbarGroup>

            {tier >= 1 && (
                <>
                    <ToolbarSeparator />

                    <ToolbarGroup>{tier1Buttons}</ToolbarGroup>
                </>
            )}

            {tier >= 2 && (
                <>
                    <ToolbarSeparator />

                    <ToolbarGroup>{tier2Buttons}</ToolbarGroup>
                </>
            )}

            {tier >= 3 && (
                <>
                    <ToolbarSeparator />

                    <ToolbarGroup>{tier3Buttons}</ToolbarGroup>
                </>
            )}

            {(tier >= 4 || tier >= 5 || tier >= 6) && (
                <>
                    {tier >= 4 && showZyVersionToolbar && (
                        <>
                            <ToolbarSeparator />
                            <ToolbarGroup className="shrink-0">{splitViewButtons}</ToolbarGroup>
                            <ToolbarSeparator />
                        </>
                    )}

                    <ToolbarGroup>
                        {tier >= 4 && <>{tier4Buttons}</>}
                        {tier >= 5 && <>{tier5Buttons}</>}
                        {tier >= 6 && <>{tier6Buttons}</>}
                    </ToolbarGroup>
                </>
            )}

            {overflowGroups.length > 0 && (
                <>
                    <ToolbarSeparator />

                    <ToolbarGroup>
                        <OverflowMenu>{overflowGroups}</OverflowMenu>
                    </ToolbarGroup>
                </>
            )}

            <Spacer />

            {isMobile && <ToolbarSeparator />}

            {enableCodeEditor && (
                <>
                    <ToolbarSeparator />

                    <ToolbarGroup>
                        <Button
                            data-style="ghost"
                            role="button"
                            data-active-state="false"
                            tooltip="Switch to code editor"
                            type="button"
                            onClick={() => onToggleCodeEditor && onToggleCodeEditor()}
                            aria-label="Switch to code editor"
                        >
                            <CodeIcon className="size-4"></CodeIcon>
                        </Button>
                    </ToolbarGroup>
                </>
            )}

            <ToolbarSeparator />

            <ToolbarGroup>
                <SearchReplacePopover isSplitView={isSplitView} />
            </ToolbarGroup>
        </>
    );
};

const MobileToolbarContent = ({ type, onBack }: { type: 'highlighter' | 'link'; onBack: () => void }) => (
    <>
        <ToolbarGroup>
            <Button
                data-style="ghost"
                onClick={onBack}
            >
                <ArrowLeftIcon className="tiptap-button-icon" />
                {type === 'highlighter' ? <HighlighterIcon className="tiptap-button-icon" /> : <LinkIcon className="tiptap-button-icon" />}
            </Button>
        </ToolbarGroup>

        <ToolbarSeparator />

        {type === 'highlighter' ? <ColorHighlightPopoverContent /> : <LinkContent />}
    </>
);

function CollaborationLoadingOverlay({ message }: { message?: string }) {
    return (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-white/80">
            <LoaderCircle
                className="animate-spin text-gray-400"
                size={32}
            />
            {message ? <p className="text-sm text-gray-600">{message}</p> : null}
        </div>
    );
}

function CollaborationPresenceTopBar({ collabProvider, editor }: { collabProvider: TiptapCollabProvider | null; editor: Editor | null }) {
    if (!collabProvider) {
        return null;
    }

    return (
        <GlobalTopBar.StartTrailing>
            <CollaborationPresence
                collabProvider={collabProvider}
                editor={editor}
            />
        </GlobalTopBar.StartTrailing>
    );
}

const TiptapEditorCollaboration = React.forwardRef(function TiptapEditorCollaboration(
    props: TiptapEditorProps,
    ref: React.ForwardedRef<TiptapEditorHandle>,
) {
    const { ydoc, collabProvider, innerEditorKey, isSynced, isReauthenticating } = useTiptapCollaborationProvider(props.tiptapDocumentName);

    if (!isSynced) {
        return (
            <div className="content-wrapper relative flex flex-col">
                <CollaborationLoadingOverlay />
            </div>
        );
    }

    return (
        <div className="content-wrapper relative flex min-h-0 flex-1 flex-col">
            <TiptapEditorInner
                key={innerEditorKey}
                ref={ref}
                {...props}
                ydoc={ydoc}
                collabProvider={collabProvider}
            />
            {isReauthenticating ? <CollaborationLoadingOverlay message="Reauthenticating…" /> : null}
        </div>
    );
});

const TiptapEditorInner = React.forwardRef<TiptapEditorHandle, TiptapEditorInnerProps>(function TiptapEditorInner(props, ref) {
    const isMobile = useMobile();
    const windowSize = useWindowSize();
    const [mobileView, setMobileView] = React.useState<'main' | 'highlighter' | 'link'>('main');
    const [toolbarElement, setToolbarElement] = React.useState<HTMLDivElement | null>(null);
    const editable = props.editable ?? true;

    const toolbarRefCallback = React.useCallback((node: HTMLDivElement | null) => {
        setToolbarElement(node);
    }, []);

    const toolbarTierWidth = useToolbarWidth(toolbarElement, windowSize.width);
    const toolbarHeight = useToolbarHeight(toolbarElement);
    const ydoc = props.ydoc;
    const [isEditorReady, setIsEditorReady] = React.useState(false);

    // Split View
    const [activeEditor, setActiveEditor] = React.useState<Editor | null>(null);

    // ZyVersion
    const [showZyversionDeleteDialog, setShowZyversionDeleteDialog] = React.useState(false);

    // Protected node delete
    const [showProtectedNodeDeleteDialog, setShowProtectedNodeDeleteDialog] = React.useState(false);
    const [protectedDeleteNodes, setProtectedDeleteNodes] = React.useState<ProtectedNodeDeletionInfo[]>([]);
    const [referencedDeleteNodes, setReferencedDeleteNodes] = React.useState<ReferencedResourceDeletionInfo[]>([]);
    const protectedDeleteResolveRef = React.useRef<((confirmed: boolean) => void) | null>(null);

    const handleProtectedDeletePrompt = React.useCallback((ctx: ProtectedNodeDeletePromptContext) => {
        protectedDeleteResolveRef.current = ctx.resolve;
        setProtectedDeleteNodes(ctx.protectedNodesToDelete);
        setReferencedDeleteNodes(ctx.referencedNodesToDelete);
        setShowProtectedNodeDeleteDialog(true);
    }, []);

    const finishProtectedDeletePrompt = (confirmed: boolean) => {
        setShowProtectedNodeDeleteDialog(false);
        setProtectedDeleteNodes([]);
        setReferencedDeleteNodes([]);
        const resolve = protectedDeleteResolveRef.current;
        protectedDeleteResolveRef.current = null;
        resolve?.(confirmed);
    };

    // Show loading until editor is ready and collaboration is synced if collaborating.
    const isLoading = !isEditorReady || ('isInitialContentLoaded' in props && !props.isInitialContentLoaded);

    const COLLAB_LABEL_REVEAL_MS = 3000;
    const [revealCollabLabels, setRevealCollabLabels] = React.useState(false);
    const labelRevealTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const triggerLabelReveal = React.useCallback(() => {
        setRevealCollabLabels(true);
        if (labelRevealTimerRef.current !== null) {
            clearTimeout(labelRevealTimerRef.current);
        }
        labelRevealTimerRef.current = setTimeout(() => {
            setRevealCollabLabels(false);
            labelRevealTimerRef.current = null;
        }, COLLAB_LABEL_REVEAL_MS);
    }, []);

    React.useEffect(
        () => () => {
            if (labelRevealTimerRef.current !== null) {
                clearTimeout(labelRevealTimerRef.current);
            }
        },
        [],
    );

    const handleEditorReady = React.useCallback(
        (readyEditor: Editor) => {
            readyEditor.commands.focus('start');
            if (props.useCollaboration && props.collabProvider) {
                queueMicrotask(triggerLabelReveal);
            }
        },
        [props.useCollaboration, props.collabProvider, triggerLabelReveal],
    );

    const baseExtensionOptions = React.useMemo<ExtensionOptions>(() => {
        const { protectedNode: userProtectedNode, zyVersion: userZyVersion, ...restExtensionOptions } = props.extensionOptions ?? {};
        return {
            mathematics: {
                renderer: 'mathjax',
            },
            imageUpload: {
                accept: ACCEPTED_IMAGE_TYPES,
                maxSize: MAX_FILE_SIZE,
                limit: 1,
                upload: handleImageUpload,
                onError: error => {
                    console.error('Upload failed:', error);
                    toast.error('Upload failed', {
                        description: (error as Error).message,
                    });
                },
            },
            details: {
                persist: true,
                HTMLAttributes: {
                    class: 'details',
                },
            },
            zyVersion: {
                onHiddenZyVersionDeleted: () => {
                    setShowZyversionDeleteDialog(true);
                },
                ...userZyVersion,
            },
            protectedNode: {
                onProtectedDeletePrompt: handleProtectedDeletePrompt,
                ...userProtectedNode,
            },
            ...restExtensionOptions,
        };
    }, [props.extensionOptions, handleProtectedDeletePrompt]);

    const editor = useEditor({
        immediatelyRender: false,
        editable,
        editorProps: getEditorProps(props.editorProps),
        extensions: getExtensions(ydoc, baseExtensionOptions, props.omitExtensions ?? [], props.collabProvider), // eslint-disable-line react-hooks/refs -- TODO: Rewrite this so we don't need to disable this rule
        content: props.content,

        enableExtensionDispatchTransaction: true,

        // --- Event Handlers ---
        onCreate: ({ editor }) => {
            migrateZybookMathStrings(editor);
            syncMaImageAltTextInEditor(editor);
            if (props.onCreate) {
                props.onCreate({ editor, collabProvider: props.collabProvider ?? undefined });
            }
            setIsEditorReady(true);
            setActiveEditor(editor);

            const waitingForInitialContent = 'isInitialContentLoaded' in props && !props.isInitialContentLoaded;
            if (!waitingForInitialContent) {
                handleEditorReady(editor);
            }
        },
        onUpdate: ({ editor }) => {
            if (props.onUpdate) {
                props.onUpdate(editor);
            }
        },
        onTransaction: props.onTransaction,
        onDelete: props.onDelete,
    });

    React.useEffect(() => {
        if (!editor) {
            return;
        }
        editor.setEditable(editable);
    }, [editor, editable]);

    React.useEffect(() => {
        const provider = props.collabProvider;
        const awareness = provider?.awareness;
        if (!awareness) return;

        const localClientId = awareness.clientID;
        const seenClientIds = new Set<number>();
        let seeded = false;

        const handleChange = () => {
            const states = awareness.getStates();
            if (!seeded) {
                for (const clientId of states.keys()) {
                    if (clientId !== localClientId) {
                        seenClientIds.add(clientId);
                    }
                }
                seeded = true;
                return;
            }
            let hasNew = false;
            for (const clientId of states.keys()) {
                if (clientId === localClientId || seenClientIds.has(clientId)) continue;
                seenClientIds.add(clientId);
                hasNew = true;
            }
            if (hasNew) {
                triggerLabelReveal();
            }
        };

        handleChange();
        awareness.on('change', handleChange);
        return () => {
            awareness.off('change', handleChange);
        };
    }, [props.collabProvider, triggerLabelReveal]);

    const { isSplitView, splitEditors, isSyncScroll, handleToggleSyncScroll, handleSyncScrollPositions, handleToggleSplitView } =
        useSplitView({
            editor,
            activeEditor,
            setActiveEditor,
            useCollaboration: props.useCollaboration,
            ydoc,
            collabProvider: props.collabProvider,
            baseExtensionOptions,
            omitExtensions: props.omitExtensions ?? [],
            getExtensions,
            getEditorProps,
        });

    const splitTransitionRef = React.useRef<'enter' | 'exit' | null>(null);
    const [isSplitViewTransitioning, setIsSplitViewTransitioning] = React.useState(false);
    const [isVersionFilterLoading, setIsVersionFilterLoading] = React.useState(false);
    const zyVersionFilterContextValue = React.useMemo(() => ({ setVersionFilterLoading: setIsVersionFilterLoading }), []);

    React.useImperativeHandle(
        ref,
        () => ({
            getEditorForPreviewVersion(previewVersion?: string): Editor | null {
                if (!editor) {
                    return null;
                }
                if (!isSplitView || splitEditors.length === 0) {
                    return editor;
                }
                const key = previewVersion?.trim();
                if (key) {
                    const match = splitEditors.find(e => e.version.trim() === key);
                    if (match) {
                        return match.editor;
                    }
                }
                return activeEditor ?? editor;
            },
            scrollToContentResourceGuidForPreviewExit(guid: string, previewVersion?: string): void {
                if (!editor) {
                    return;
                }

                // Split view editors don't have a sectionId, so we use the main editor's sectionId for composite guid construction
                const sectionIdForComposite = editor.state.doc.attrs?.sectionId as string | number | null | undefined;
                if (!isSplitView || splitEditors.length === 0) {
                    scrollEditorToContentResourceGuid(editor, guid, sectionIdForComposite);
                    return;
                }
                if (isSyncScroll) {
                    withScrollSyncApply(() => {
                        for (const { editor: paneEditor } of splitEditors) {
                            scrollEditorToContentResourceGuid(paneEditor, guid, sectionIdForComposite);
                        }
                    });
                    return;
                }
                const key = previewVersion?.trim();
                let target: Editor | null = null;
                if (key) {
                    const match = splitEditors.find(e => e.version.trim() === key);
                    if (match) {
                        target = match.editor;
                    }
                }
                if (!target) {
                    target = activeEditor ?? editor;
                }
                scrollEditorToContentResourceGuid(target, guid, sectionIdForComposite);
            },
        }),
        [editor, isSplitView, splitEditors, activeEditor, isSyncScroll],
    );

    const bodyRect = useCursorVisibility({
        editor,
        overlayHeight: toolbarHeight,
    });

    const toolbarView = isMobile ? mobileView : 'main';

    React.useLayoutEffect(() => {
        if (!isSplitViewTransitioning) return;
        const direction = splitTransitionRef.current;
        // Exiting split view → main editor visible again
        const mainEditorVisible = direction === 'exit' && !isSplitView && splitEditors.length === 0 && activeEditor === editor;
        // Entering split view → panes mounted (optional, separate case)
        const splitPanesReady = direction === 'enter' && isSplitView && splitEditors.length > 0;
        if (!mainEditorVisible && !splitPanesReady) return;
        // Wait one frame so the newly visible DOM actually paints
        requestAnimationFrame(() => {
            setIsSplitViewTransitioning(false);
            splitTransitionRef.current = null;
        });
    }, [isSplitView, splitEditors.length, activeEditor, editor, isSplitViewTransitioning]);

    React.useEffect(() => {
        if (!isSplitView || props.useCollaboration || !activeEditor || splitEditors.length <= 1) return;

        const syncSelectionToOthers = () => {
            const sel = activeEditor.state.selection;
            const payload = isNodeSelection(sel) ? { type: 'node' as const, pos: sel.from } : { from: sel.from, to: sel.to };
            for (const { editor: ed } of splitEditors) {
                if (ed !== activeEditor && 'applySharedSelection' in ed.commands) {
                    ed.commands.applySharedSelection(payload);
                }
            }
        };
        syncSelectionToOthers();
        activeEditor.on('selectionUpdate', syncSelectionToOthers);
        return () => {
            activeEditor.off('selectionUpdate', syncSelectionToOthers);
        };
    }, [isSplitView, props.useCollaboration, activeEditor, splitEditors]);

    // When a native DOM focusable (input, button, etc.) has focus and the user presses Tab,
    // the browser's built-in tab order skips paragraphs. This intercepts Tab in capture phase
    // and redirects focus into the editor if a paragraph comes next in document order.
    React.useEffect(() => {
        if (!editor) return;

        const handleTabFromNativeFocusable = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            const active = document.activeElement as HTMLElement | null;
            if (!active) return;

            // Only intercept when focus is outside the contenteditable.
            if (active === editor.view.dom) return;

            if (event.shiftKey) {
                const prevPara = findPreviousParagraphBeforeElement(editor, active);
                if (!prevPara) return;

                const prevParaDom = editor.view.nodeDOM(prevPara.pos) as HTMLElement | null;
                const prevNonPara = findPreviousFocusableElement(document.body, active);

                // The paragraph is "closer" going backward if it appears after the non-para focusable.
                const paraIsCloser =
                    !prevNonPara || (prevParaDom && prevNonPara.compareDocumentPosition(prevParaDom) & Node.DOCUMENT_POSITION_FOLLOWING);

                if (paraIsCloser) {
                    event.preventDefault();
                    const endPos = prevPara.pos + prevPara.node.nodeSize - 1;
                    editor.commands.focus(endPos, { scrollIntoView: true });
                }
            } else {
                const nextPara = findNextParagraphAfterElement(editor, active);
                if (!nextPara) return;

                const nextParaDom = editor.view.nodeDOM(nextPara.pos) as HTMLElement | null;
                const nextNonPara = findNextFocusableElement(document.body, active);

                const paraIsFirst =
                    !nextNonPara || (nextParaDom && nextNonPara.compareDocumentPosition(nextParaDom) & Node.DOCUMENT_POSITION_PRECEDING);

                if (paraIsFirst) {
                    event.preventDefault();
                    const endPos = nextPara.pos + nextPara.node.nodeSize - 1;
                    editor.commands.focus(endPos, { scrollIntoView: true });
                }
            }
        };
        document.addEventListener('keydown', handleTabFromNativeFocusable, true);
        return () => document.removeEventListener('keydown', handleTabFromNativeFocusable, true);
    }, [editor]);

    const handleRestoreZyversionDelete = () => {
        if (isSplitView) {
            activeEditor?.commands.undo();
        } else {
            editor?.commands.undo();
        }
        setShowZyversionDeleteDialog(false);
    };

    const zybookCodeForReferenceLinks = (activeEditor ?? editor)?.state.doc.attrs?.zybookCode as string | undefined;

    const referencedDeleteLinks = React.useMemo(() => {
        const currentEditor = activeEditor ?? editor;
        const tocSectionTitleByMaDocumentId = currentEditor
            ? getOrCreateReferenceStore(currentEditor).getState().tocSectionTitleByMaDocumentId
            : {};
        const links: { href: string | null; label: string; key: string }[] = [];

        for (const node of referencedDeleteNodes) {
            for (const inbound of node.inboundReferences) {
                const chapter = inbound.source?.chapter_number;
                const section = inbound.source?.section_number;
                const sourceMaDocumentId = inbound.source?.ma_document_id;
                const sectionTitle = sourceMaDocumentId != null ? tocSectionTitleByMaDocumentId[String(sourceMaDocumentId)]?.trim() : null;

                const label = `${chapter}.${section} ${sectionTitle}`;

                const href =
                    zybookCodeForReferenceLinks && sourceMaDocumentId != null && inbound.source?.node_id
                        ? `${window.location.origin}/books/${encodeURIComponent(zybookCodeForReferenceLinks)}/${sourceMaDocumentId}?nodeId=${encodeURIComponent(inbound.source.node_id)}`
                        : null;

                links.push({
                    href,
                    label,
                    key: `${node.nodeId}:${inbound.source?.section_guid ?? ''}:${inbound.source?.node_id ?? ''}`,
                });
            }
        }

        return links;
    }, [referencedDeleteNodes, zybookCodeForReferenceLinks, activeEditor, editor]);

    const editorContext = React.useMemo(() => ({ editor: activeEditor }), [activeEditor]);
    // Toolbar buttons read editor.isEditable while rendering, but useTiptapEditor only re-renders them when the
    // editor instance changes. Remount the toolbar when editability flips so no button keeps a stale read-only state.
    const toolbarReadonly = useIsEditorReadonly(activeEditor ?? editor);
    const contentWrapperRef = React.useRef<HTMLDivElement>(null);

    return (
        <>
            {props.useCollaboration && props.collabProvider && (
                <CollaborationPresenceTopBar
                    collabProvider={props.collabProvider}
                    editor={activeEditor ?? editor}
                />
            )}
            <ZyVersionFilterContext.Provider value={zyVersionFilterContextValue}>
                <EditorContext.Provider value={editorContext}>
                    <div className="tiptap-editor-shell">
                        <Toolbar
                            key={`${activeEditor?.instanceId ?? 'main-editor'}-${toolbarReadonly ? 'readonly' : 'editable'}`}
                            ref={toolbarRefCallback}
                            style={
                                isMobile
                                    ? {
                                          bottom: `calc(100% - ${windowSize.height - bodyRect.y}px)`,
                                      }
                                    : {}
                            }
                        >
                            {toolbarView === 'main' ? (
                                <MainToolbarContent
                                    onHighlighterClick={() => setMobileView('highlighter')}
                                    onLinkClick={() => setMobileView('link')}
                                    onToggleCodeEditor={props.onToggleCodeEditor}
                                    isMobile={isMobile}
                                    enableCodeEditor={props.enableCodeEditor}
                                    isSplitView={isSplitView}
                                    isSyncScroll={isSyncScroll}
                                    onToggleSplitView={() => {
                                        splitTransitionRef.current = isSplitView ? 'exit' : 'enter';
                                        setIsSplitViewTransitioning(true);
                                        requestAnimationFrame(() => {
                                            const didToggle = handleToggleSplitView();
                                            if (!didToggle && splitTransitionRef.current === 'enter') {
                                                setIsSplitViewTransitioning(false);
                                                splitTransitionRef.current = null;
                                            }
                                        });
                                    }}
                                    onToggleSyncScroll={handleToggleSyncScroll}
                                    onSyncScroll={handleSyncScrollPositions}
                                    width={toolbarTierWidth}
                                    mainEditor={editor}
                                    showZyVersionToolbar={props.showZyVersionToolbar ?? true}
                                />
                            ) : (
                                <MobileToolbarContent
                                    type={toolbarView === 'highlighter' ? 'highlighter' : 'link'}
                                    onBack={() => setMobileView('main')}
                                />
                            )}
                        </Toolbar>

                        {editor && !isSplitView && <SnapshotCompareUserLegend editor={editor} />}

                        <FullscreenBoundaryContext.Provider value={{ contentWrapperRef }}>
                            <div
                                ref={contentWrapperRef}
                                className={`content-wrapper relative flex flex-col${isSplitView ? ' is-split-view' : ''}${revealCollabLabels ? ' collab-labels-reveal' : ''}`}
                            >
                                {/* Loading indicator */}
                                {(isLoading || isSplitViewTransitioning || isVersionFilterLoading) && (
                                    <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/80 dark:bg-gray-900/80">
                                        <LoaderCircle
                                            className="animate-spin text-gray-400"
                                            size={32}
                                        />
                                    </div>
                                )}

                                {/* Main editor */}
                                <div
                                    style={isSplitView ? { display: 'none' } : undefined}
                                    aria-hidden={isSplitView}
                                    className={props.leftGutter ? 'zybooks-editor-with-gutter' : undefined}
                                >
                                    {isSplitView ? null : props.leftGutter}
                                    <div className={props.leftGutter ? 'zybooks-editor-column' : undefined}>
                                        {editor && <TiptapEditorPlugins editor={editor} />}

                                        <EditorContent
                                            editor={editor}
                                            role="presentation"
                                            className="zybooks-editor-content"
                                        />
                                    </div>
                                </div>

                                {/* Split view editor */}
                                {isSplitView && (
                                    <SplitViewPanes
                                        editors={splitEditors}
                                        syncScroll={isSyncScroll}
                                        onFocus={setActiveEditor}
                                    />
                                )}

                                {editor && !isSplitView && <ZyVersionSelectionLegend editor={editor} />}
                            </div>
                        </FullscreenBoundaryContext.Provider>
                    </div>
                </EditorContext.Provider>
            </ZyVersionFilterContext.Provider>

            <Dialog
                open={showZyversionDeleteDialog}
                onOpenChange={(open: any) => setShowZyversionDeleteDialog(open)}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Removed hidden content</DialogTitle>
                    </DialogHeader>
                    <p>A hidden zyVersion content has been deleted. If this is a mistake, click undo to revert your last change.</p>
                    <DialogFooter>
                        <ZybooksButton
                            variant="outline"
                            size="sm"
                            onClick={() => setShowZyversionDeleteDialog(false)}
                        >
                            Keep as is
                        </ZybooksButton>
                        <ZybooksButton
                            size="sm"
                            onClick={handleRestoreZyversionDelete}
                        >
                            Undo
                        </ZybooksButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={showProtectedNodeDeleteDialog}
                onOpenChange={(open: boolean) => {
                    if (!open && protectedDeleteResolveRef.current) {
                        finishProtectedDeletePrompt(false);
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {protectedDeleteNodes.length === 1 ? (
                            <p>This will delete a {protectedDeleteNodes[0].typeName} content block.</p>
                        ) : null}

                        {protectedDeleteNodes.length > 1 ? (
                            <div className="space-y-2">
                                <p>These content blocks will be deleted:</p>
                                <ul className="max-h-48 list-outside list-disc space-y-1.5 overflow-y-auto pl-5">
                                    {protectedDeleteNodes.map((n, i) => (
                                        <li key={i}>{n.typeName}</li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}

                        {referencedDeleteLinks.length > 0 ? (
                            <div className="space-y-2">
                                <p>This resource is being referenced by:</p>
                                <ul className="max-h-48 list-outside list-disc space-y-1.5 overflow-y-auto pl-5">
                                    {referencedDeleteLinks.map(link => (
                                        <li key={link.key}>
                                            {link.href ? (
                                                <a
                                                    href={link.href}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-primary underline"
                                                >
                                                    {link.label}
                                                </a>
                                            ) : (
                                                link.label
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}

                        <p>Continue?</p>
                    </div>

                    <DialogFooter>
                        <ZybooksButton
                            variant="outline"
                            size="sm"
                            onClick={() => finishProtectedDeletePrompt(false)}
                        >
                            Cancel
                        </ZybooksButton>
                        <ZybooksButton
                            size="sm"
                            onClick={() => finishProtectedDeletePrompt(true)}
                        >
                            Delete
                        </ZybooksButton>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
});

export const TiptapEditor = React.forwardRef<TiptapEditorHandle, TiptapEditorProps>(function TiptapEditor(props, ref) {
    if (props.useCollaboration) {
        return (
            <TiptapEditorCollaboration
                ref={ref}
                {...props}
            />
        );
    }

    return (
        <TiptapEditorInner
            ref={ref}
            {...props}
            ydoc={null}
            collabProvider={null}
        />
    );
});

TiptapEditor.displayName = 'TiptapEditor';
TiptapEditorInner.displayName = 'TiptapEditorInner';
